import api from './api';
import { logEvent as monitoringLogEvent } from './monitoring';

class Analytics {
  private static instance: Analytics;
  private sessionId: string;
  private initialized: boolean = false;
  private initializationAttempted: boolean = false;
  private isDev = import.meta.env.DEV;
  private offlineQueue: Array<{name: string, properties?: Record<string, any>}> = [];

  private constructor() {
    this.sessionId = crypto.randomUUID();
    window.addEventListener('online', this.processOfflineQueue.bind(this));
  }

  static getInstance(): Analytics {
    if (!Analytics.instance) {
      Analytics.instance = new Analytics();
    }
    return Analytics.instance;
  }

  private async initialize(): Promise<boolean> {
    if (this.initialized || this.initializationAttempted) {
      return this.initialized;
    }

    this.initializationAttempted = true;

    try {
      // Check if API is available
      const response = await api.request('/api/health');
      if (response.success) {
        this.initialized = true;
        return true;
      }
      return false;
    } catch (err) {
      if (this.isDev) {
        console.warn('Analytics disabled: API not available');
      }
      return false;
    }
  }

  private async processOfflineQueue() {
    if (this.offlineQueue.length === 0) return;

    const events = [...this.offlineQueue];
    this.offlineQueue = [];

    for (const event of events) {
      try {
        await this.trackEvent(event.name, event.properties);
      } catch (err) {
        // If still failing, add back to queue
        this.offlineQueue.push(event);
        if (this.isDev) {
          console.warn('Failed to process offline event:', err);
        }
      }
    }
  }

  async trackEvent(name: string, properties?: Record<string, any>) {
    try {
      // Try to initialize if not already done
      if (!await this.initialize()) {
        // Store event for later if offline
        if (!navigator.onLine) {
          this.offlineQueue.push({ name, properties });
          if (this.isDev) {
            console.log('Stored offline event:', { name, properties });
          }
        }
        return;
      }

      // Send analytics event to API
      await api.request('/api/analytics/events', {
        method: 'POST',
        body: JSON.stringify({
          event_name: name,
          properties,
          session_id: this.sessionId,
          timestamp: new Date().toISOString(),
        }),
      });
    } catch (err) {
      // Store event for later if offline
      if (!navigator.onLine) {
        this.offlineQueue.push({ name, properties });
        if (this.isDev) {
          console.log('Stored offline event:', { name, properties });
        }
        return;
      }

      if (this.isDev) {
        console.error('Failed to track analytics event:', err);
      }

      // Log to monitoring system but don't throw
      monitoringLogEvent({
        level: 'error',
        message: 'Failed to track analytics event',
        metadata: {
          eventName: name,
          properties,
          error: err instanceof Error ? err.message : 'Unknown error'
        }
      }).catch(() => {}); // Ignore monitoring errors
    }
  }

  async trackPageView(path: string) {
    await this.trackEvent('page_view', { path });
  }

  async trackError(error: Error) {
    await this.trackEvent('error', {
      message: error.message,
      stack: error.stack,
      url: window.location.href,
    });
  }
}

export const analytics = Analytics.getInstance();
